import { Component, Input, Output, Signal, EventEmitter, ViewChild, ViewEncapsulation, Optional, signal, effect, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  RowClassArgs,
  RowClassFn,
  GridModule,
  GridComponent,
  DataBindingDirective,
  DataStateChangeEvent,
  PageChangeEvent,
  KENDO_GRID_PDF_EXPORT,
  KENDO_GRID_EXCEL_EXPORT,
  ColumnMenuSettings,
  GroupRowArgs,
} from '@progress/kendo-angular-grid';
import {
  process,
  State,
  CompositeFilterDescriptor,
  FilterDescriptor,
  GroupResult,
  SortDescriptor,
  GroupDescriptor,
} from '@progress/kendo-data-query';
import { ITag } from '../../_models/tag.model';
import { Router } from '@angular/router';
import { TagCacheService } from '../../services/tag-cache.service';
import {
  IonActionSheet,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPopover,
  IonTitle,
  IonToolbar,
  ToastController
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  arrowUpOutline,
  arrowDownOutline,
  closeCircleOutline,
  removeOutline,
  addOutline,
  refreshOutline,
  resizeOutline,
  funnelOutline,
  filterOutline,
  lockClosedOutline,
  pinOutline,
  eyeOutline,
  swapVerticalOutline,
  copyOutline,
  chevronForwardOutline,
  searchCircleOutline,
  close,
  checkmarkOutline,
  expandOutline,
  contractOutline,
} from 'ionicons/icons';
import type { ActionSheetButton } from '@ionic/core/dist/types/components';
import { Capacitor } from '@capacitor/core';
import { fileExcelIcon, filePdfIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { PathPipe } from '../../pipes/path.pipe';
import { MobileTimezoneDatePipe } from '../../pipes/mobile-timezone-date.pipe';
import { KendoMultiFilterMenuComponent } from '../kendo-multi-filter-menu/kendo-multi-filter-menu.component';
import { DataService } from '../../services/data.service';
import { ClipboardService } from '../../services/clipboard.service';
import { MobileTimezoneService } from '../../services/mobile-timezone.service';

@Component({
  selector: 'lib-kendo-grid-parent',
  standalone: true,
  imports: [
    CommonModule,
    GridModule,
    KENDO_GRID_EXCEL_EXPORT,
    KENDO_GRID_PDF_EXPORT,
    PathPipe,
    MobileTimezoneDatePipe,
    KendoMultiFilterMenuComponent,
    IonActionSheet,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButtons,
    IonPopover,
    IonList,
    IonItem,
    IonLabel,
    IonIcon,
    IonButton,
  ],
  templateUrl: './kendo-grid-parent.component.html',
  styleUrl: './kendo-grid-parent.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class KendoGridParentComponent implements OnInit, OnDestroy {
  private readonly enabledPagerSettings = {
    buttonCount: 5,
    info: true,
    type: 'numeric' as const,
    pageSizes: [20, 50, 100],
    previousNext: true,
    responsive: true,
  };

    public menuSettings: ColumnMenuSettings = {
    lock: true,
    stick: true,
    setColumnPosition: { expanded: true },
    autoSizeColumn: true,
    autoSizeAllColumns: true,
  };

  columnMenuModalOpen = false;
  columnMenuModalTitle = '';
  columnMenuModalService: any | null = null;
  columnMenuModalColumn: any | null = null;
  private columnMenuModalOriginalClose: (() => void) | null = null;
  private columnMenuModalOpening = false;

   @Input() tagGrid = false;
  @Input() feedStateBackToParent = false;

  // 🔹 NEW: two-way state
  @Input() initialState?: State;
  @Output() stateChange = new EventEmitter<State>();
  @Output() investigationClick = new EventEmitter<any>();

  // (optional) if you still want external [sort] binding
  @Input() sort: any[] = [];
  @Output() sortChange = new EventEmitter<any[]>();

  public fileExcelIcon: SVGIcon = fileExcelIcon;
  public filePdfIcon: SVGIcon = filePdfIcon;

  longPressTimer: any = null;
  longPressDuration = 500; // ms

  constructor(
    private router: Router,
    private tagCacheService: TagCacheService,
    private toastCtrl: ToastController,
    private dataService: DataService,
    private clipboardService: ClipboardService,
    private mobileTimezoneService: MobileTimezoneService
  ) {
    addIcons({
      arrowUpOutline,
      arrowDownOutline,
      closeCircleOutline,
      removeOutline,
      addOutline,
      refreshOutline,
      resizeOutline,
      funnelOutline,
      filterOutline,
      lockClosedOutline,
      pinOutline,
      eyeOutline,
      swapVerticalOutline,
      copyOutline,
      chevronForwardOutline,
      searchCircleOutline,
      close,
      checkmarkOutline,
      expandOutline,
      contractOutline,
    });

    effect(() => {
      // debug
      // console.log('[EFFECT] gridState:', this.gridState());
    });
    this.allData = this.allData.bind(this);
  }

  resolvePath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
  }

  gridDateFormat(column: any): string {
    const raw = typeof column?.format === 'string' ? column.format.trim() : '';
    const match = raw.match(/^\{0:(.+)\}$/);
    return match?.[1] || 'short';
  }

  resolveDateSiteId(dataItem: any): number | null {
    const candidates = [
      dataItem?.SiteId,
      dataItem?.Site?.Id,
      dataItem?.Asset?.SiteId,
      dataItem?.Asset?.Site?.Id,
      dataItem?.gate?.SiteId,
      dataItem?.gate?.Site?.Id,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate;
      }
    }

    return null;
  }

  statusPill(
    column: any,
    dataItem: any
  ): { text: string; color: string; background: string; class?: string } | null {
    const render = column?.display?.render;
    if (!render || (render.type !== 'statusPill' && render.type !== 'categoryPill' && render.type !== 'chip')) return null;

    const labels = render.labels ?? {};
    const colors = render.colors ?? {};
    const background = render.background ?? {};

    if (render.criticalField || render.alarmField) {
      const isCritical = !!this.resolvePath(dataItem, render.criticalField ?? '');
      const isAlarm = !!this.resolvePath(dataItem, render.alarmField ?? '');
      if (isCritical) {
        return {
          text: labels.critical ?? 'Critical',
          color: colors.critical ?? '#d32f2f',
          background: background.critical ?? '#ffebee',
          class: 'status-pill--critical',
        };
      }
      if (isAlarm) {
        return {
          text: labels.alarm ?? 'Alarm',
          color: colors.alarm ?? '#f57c00',
          background: background.alarm ?? '#fff3e0',
          class: 'status-pill--alarm',
        };
      }
      return {
        text: labels.normal ?? 'Normal',
        color: colors.normal ?? '#18963b',
        background: background.normal ?? '#e9f6ee',
        class: 'status-pill--normal',
      };
    }

    const raw = this.resolvePath(dataItem, column.field);

    if (column.field === 'Severity' || render.type === 'severityPill') {
      const str = String(raw ?? '').toLowerCase();
      const isCritical = str.includes('critical');
      const isAlarm = str.includes('alarm') || str.includes('warning');
      const isAlarmOrCritical = isCritical || isAlarm;

      if (isAlarmOrCritical) {
        if (render.showCategory) {
          const catRaw =
            dataItem?.EffectiveAlarmCategory ??
            dataItem?.Category ??
            dataItem?.EffectiveAlarmCategoryName ??
            this.resolvePath(dataItem, 'EffectiveAlarmCategory') ??
            this.resolvePath(dataItem, 'Category');
          return this.buildCategoryPill(column, dataItem, catRaw);
        }

        if (isCritical) {
          return {
            text: labels.critical ?? 'Critical',
            color: colors.critical ?? '#d32f2f',
            background: background.critical ?? '#ffebee',
            class: 'status-pill--critical',
          };
        }

        return {
          text: labels.alarm ?? 'Alarm',
          color: colors.alarm ?? '#f57c00',
          background: background.alarm ?? '#fff3e0',
          class: 'status-pill--alarm',
        };
      }

      return {
        text: labels.normal ?? 'Normal',
        color: colors.normal ?? '#18963b',
        background: background.normal ?? '#e9f6ee',
        class: 'status-pill--normal',
      };
    }

    return this.buildCategoryPill(column, dataItem, raw);
  }

  private buildCategoryPill(
    column: any,
    dataItem: any,
    rawCat?: any,
    catIdRawParam?: any
  ): { text: string; color: string; background: string; class?: string } {
    const render = column?.display?.render;
    const labels = render?.labels ?? {};
    const colors = render?.colors ?? {};
    const background = render?.background ?? {};

    const catIdRaw =
      catIdRawParam ??
      this.resolvePath(dataItem, render?.idField ?? 'EffectiveAlarmCategoryId') ??
      dataItem?.EffectiveAlarmCategoryId ??
      dataItem?.CategoryId ??
      dataItem?.AlarmCategoryId ??
      null;
    const catId = catIdRaw != null && !isNaN(+catIdRaw) && +catIdRaw !== 0 ? +catIdRaw : null;

    let textVal = rawCat != null && String(rawCat).trim() !== '' ? String(rawCat).trim() : '';

    if (!textVal && catId != null && this.dataService?.cache) {
      const cacheCategories: any[] =
        this.dataService.cache.alarmcategories ||
        (this.dataService.cache as any).alarmCategories ||
        [];
      const found = Array.isArray(cacheCategories)
        ? cacheCategories.find((c: any) => c.Id === catId || c.id === catId)
        : null;
      if (found?.Name || found?.name) {
        textVal = String(found.Name || found.name).trim();
      }
    }

    const hasValue = textVal !== '';
    const displayText = hasValue ? textVal : (labels.uncategorized ?? 'Uncategorized');
    const key = displayText.toLowerCase().replace(/[_\s-]+/g, ' ').trim();

    const categoryPresets: Record<
      string,
      { bg: string; color: string; slug: string }
    > = {
      'alarm': { bg: '#FF0000', color: '#FFFFFF', slug: 'alarm' },
      'equipment fault': { bg: '#FF0000', color: '#FFFFFF', slug: 'alarm' },
      'fault': { bg: '#FF0000', color: '#FFFFFF', slug: 'alarm' },

      'safety alarm': { bg: '#C00002', color: '#FFFFFF', slug: 'safety-alarm' },
      'safety event': { bg: '#C00002', color: '#FFFFFF', slug: 'safety-alarm' },
      'safety': { bg: '#C00002', color: '#FFFFFF', slug: 'safety-alarm' },

      'communications': { bg: '#FFC000', color: '#000000', slug: 'communications' },
      'communication': { bg: '#FFC000', color: '#000000', slug: 'communications' },
      'comms': { bg: '#FFC000', color: '#000000', slug: 'communications' },

      'operational alert': { bg: '#FFFD00', color: '#000000', slug: 'operational-alert' },
      'operational condition': { bg: '#FFFD00', color: '#000000', slug: 'operational-alert' },
      'operational': { bg: '#FFFD00', color: '#000000', slug: 'operational-alert' },

      'operator alert': { bg: '#0070C0', color: '#FFFFFF', slug: 'operator-alert' },
      'operator': { bg: '#0070C0', color: '#FFFFFF', slug: 'operator-alert' },

      'uncategorized': { bg: '#F1F5F9', color: '#64748B', slug: 'uncategorized' },
      'none': { bg: '#F1F5F9', color: '#64748B', slug: 'uncategorized' },
    };

    const preset = categoryPresets[key];

    const textColor =
      colors[key] ??
      colors.default ??
      preset?.color ??
      (hasValue ? '#1e40af' : '#64748B');

    const bgColor =
      background[key] ??
      background.default ??
      preset?.bg ??
      (hasValue ? '#dbeafe' : '#F1F5F9');

    const classList: string[] = [];
    if (preset?.slug) {
      classList.push(`status-pill--${preset.slug}`);
    } else if (hasValue) {
      const customSlug = key.replace(/\s+/g, '-');
      classList.push(`status-pill--${customSlug}`);
    } else {
      classList.push('status-pill--uncategorized');
    }

    if (catId != null) {
      classList.push(`status-pill--cat-id-${catId}`);
    }

    return {
      text: labels[key] ?? displayText,
      color: textColor,
      background: bgColor,
      class: classList.join(' '),
    };
  }

  getStatusCategory(dataItem: any): string {
    if (!dataItem) return '';
    const cat = dataItem.EffectiveAlarmCategory ?? dataItem.Category ?? dataItem.EffectiveAlarmCategoryName;
    return typeof cat === 'string' ? cat.trim() : '';
  }

  shouldShowStatusCategory(_column: any, _dataItem: any): boolean {
    return false;
  }

  statusPillFilterValue(
    filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined,
    field: string
  ): string {
    if (!filter) return '';
    const composite = filter as CompositeFilterDescriptor;
    if (!Array.isArray(composite.filters)) {
      const leaf = filter as FilterDescriptor;
      return leaf.field === field ? String(leaf.value ?? '') : '';
    }
    for (const child of composite.filters) {
      const val = this.statusPillFilterValue(child as any, field);
      if (val) return val;
    }
    return '';
  }

  onStatusPillFilterChange(
    event: Event,
    filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined,
    field: string,
    filterService: any
  ): void {
    const value = (event.target as HTMLSelectElement)?.value ?? '';
    const baseFilter = filter ?? this.gridState().filter;
    const next = this.replaceStatusFilter(baseFilter, field, value);
    const nextFilterState = next.filters.length > 0 ? next : undefined;
    const nextState: State = { ...this.gridState(), filter: nextFilterState, skip: 0 };
    this.gridState.set(nextState);
    this.emitStateIfRequested(nextState);

    try {
      filterService?.filter?.(next);
    } catch {}
    this.requestCloseColumnMenuModal();
  }

  clearStatusPillFilter(
    filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined,
    field: string,
    filterService: any
  ): void {
    const baseFilter = filter ?? this.gridState().filter;
    const next = this.replaceStatusFilter(baseFilter, field, '');
    const nextFilterState = next.filters.length > 0 ? next : undefined;
    const nextState: State = { ...this.gridState(), filter: nextFilterState, skip: 0 };
    this.gridState.set(nextState);
    this.emitStateIfRequested(nextState);

    try {
      filterService?.filter?.(next);
    } catch {}
    this.requestCloseColumnMenuModal();
  }

  private replaceStatusFilter(
    filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined,
    field: string,
    value: string
  ): CompositeFilterDescriptor {
    const composite: CompositeFilterDescriptor = filter && (filter as CompositeFilterDescriptor).filters
      ? (filter as CompositeFilterDescriptor)
      : { logic: 'and', filters: [] };

    const cleaned = this.removeFieldFilters(composite, field);
    if (value) {
      cleaned.filters.push({ field, operator: 'eq', value });
    }
    return cleaned;
  }

  private removeFieldFilters(
    filter: CompositeFilterDescriptor,
    field: string
  ): CompositeFilterDescriptor {
    const nextFilters = (filter.filters ?? [])
      .map((f) => {
        const comp = f as CompositeFilterDescriptor;
        if (Array.isArray(comp.filters)) {
          const nested = this.removeFieldFilters(comp, field);
          return nested.filters.length ? nested : null;
        }
        const leaf = f as FilterDescriptor;
        return leaf.field === field ? null : leaf;
      })
      .filter((f): f is CompositeFilterDescriptor | FilterDescriptor => !!f);

    return { logic: filter.logic ?? 'and', filters: nextFilters };
  }

  @Input() usingSignals: boolean = false;
  @Input() records: any[] = [];
  @Input() recordsAsSignals!: Signal<any[]>;
  @Optional() @Input() currentDataIndex: number | null = null;
  @Input() gridSettings: any = [];

  // 🔹 Internal reactive state used to bind the grid
  gridState = signal<State>({
    skip: 0,
    take: 20,
    sort: [],
    group: [],
    filter: undefined,
  });
  paginationEnabled = signal(true);
  pageSizeItems = [
    { text: '20', value: 20 },
    { text: '50', value: 50 },
    { text: '100', value: 100 },
    { text: 'Show all', value: 'all' },
  ] as const;
  private allGroupsCollapsed = signal(false);
  private expandedGroupKeys = signal<Set<string>>(new Set());
  private collapsedGroupKeys = signal<Set<string>>(new Set());

  @ViewChild('grid') grid?: GridComponent;

  private addedColumnsSig = signal<any[]>([]);
  private columnOverridesSig = signal<Record<string, Partial<{ hidden: boolean }>>>({});
  private columnWidthOverridesSig = signal<Record<string, number>>({});
  private gridColumnsTick = signal(0);

  @ViewChild('filtersPopover') filtersPopover?: IonPopover;
  filtersModalOpen = false;
  usePopoverFilters = false;
  filtersEvent?: Event;
  filtersButtons: ActionSheetButton[] = [];

  private isTrendIgnoreEvent(originalEvent: unknown): boolean {
    const e = originalEvent as any;

    if (typeof e?.composedPath === 'function') {
      const path = e.composedPath() as unknown[];
      for (const node of path) {
        const el = node as any;
        if (el?.getAttribute?.('data-trend-ignore') === 'true') return true;
        if (typeof el?.closest === 'function' && el.closest('[data-trend-ignore="true"]')) return true;
      }
    }

    const target = e?.target as Element | null | undefined;
    if (!target || typeof (target as any).closest !== 'function') return false;
    return !!target.closest('[data-trend-ignore="true"]');
  }

  onChevronClick(event: Event, field: string, dataItem: any): void {
    event.preventDefault();
    event.stopPropagation();

    let id: number | undefined;
    let routeBase: string | undefined;

    switch (field) {
      case 'SiteName':
        id = dataItem.Site?.Id ?? dataItem.SiteId;
        routeBase = 'site-summary';
        break;

      case 'Asset.ParentSystem.Name':
        id = dataItem.Asset?.ParentSystem?.Id;
        routeBase = 'gate-summary';
        break;

      case 'Asset.Name':
        id = dataItem.Asset?.Id;
        routeBase = 'asset-summary';
        break;
    }

    if (id && routeBase) {
      this.router.navigate([`/${routeBase}`, id, 'overview']);
    } else {
      console.warn('Navigation failed: missing ID or route base for field:', field, dataItem);
    }
  }

  onInvestigationClick(event: Event, dataItem: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.investigationClick.emit(dataItem);
  }

  shouldShowInvestigationAction(column: any, dataItem: any): boolean {
    const render = column?.display?.render;
    if (render?.type !== 'investigationAction') return false;

    const assetId = Number(dataItem?.Asset?.Id ?? dataItem?.AssetId);
    const asset = dataItem?.Asset ?? (Number.isFinite(assetId) ? this.dataService.cache?.assetsObject?.[assetId] : undefined);
    const eligibility = render?.eligibility ?? 'gse';
    const parentSystemId = Number(
      asset?.ParentSystem?.Id ??
      asset?.ParentSystemId ??
      dataItem?.ParentSystem?.Id ??
      dataItem?.ParentSystemId ??
      dataItem?.SystemId ??
      dataItem?.GateId
    );
    const isGateAsset = Number.isFinite(assetId) && Number.isFinite(parentSystemId);

    if (eligibility === 'gateAsset') {
      return isGateAsset;
    }

    if (eligibility === 'anyAsset') {
      return Number.isFinite(assetId);
    }

    const groupId = asset?.AssetType?.AssetTypeGroupId;
    return (groupId != null && groupId !== 1) || isGateAsset;
  }

  ngOnInit() {
    // Intentionally no logging here (records can be very large).
  }

  ngOnDestroy(): void {
    void this.filtersPopover?.dismiss();
  }

  newMappedSignals(): any[] {
    return [...this.recordsAsSignals()];
  }

    ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialState'] && this.initialState) {
      // Merge incoming state with current to avoid dropping defaults
      const next: State = {
        ...this.gridState(),
        ...this.initialState,
      };
      this.gridState.set(next);
    }
  }

   onDataStateChange(state: DataStateChangeEvent): void {
    const currentGroups = this.groupSignature(this.gridState().group);
    const nextGroups = this.groupSignature(state.group ?? this.gridState().group);
    const next = { ...this.gridState(), ...state };
    if (currentGroups !== nextGroups) {
      this.resetGroupExpansionState();
    }
    this.gridState.set(next);
    if (this.feedStateBackToParent) {
      this.stateChange.emit(next);  // only emit when the parent asked for it
    }
  }

  onFilterChange(filter: CompositeFilterDescriptor): void {
    const next: State = { ...this.gridState(), filter, skip: 0 };
    this.gridState.set(next);
    if (this.feedStateBackToParent) {
      this.stateChange.emit(next);
    }
  }

  onSortChange(sort: SortDescriptor[]): void {
    const next: State = { ...this.gridState(), sort };
    this.gridState.set(next);
    if (this.feedStateBackToParent) {
      this.stateChange.emit(next);
    }
  }

  onPageChange(event: PageChangeEvent): void {
    const next: State = { ...this.gridState(), skip: event.skip, take: event.take };
    this.gridState.set(next);
    if (this.feedStateBackToParent) {
      this.stateChange.emit(next);
    }
  }

  onGroupChange(group: GroupDescriptor[]): void {
    const currentGroups = this.groupSignature(this.gridState().group);
    const nextGroups = this.groupSignature(group);
    const next: State = { ...this.gridState(), group };
    if (currentGroups !== nextGroups) {
      this.resetGroupExpansionState();
    }
    this.gridState.set(next);
    if (this.feedStateBackToParent) {
      this.stateChange.emit(next);
    }
  }

  onColumnMenuFilterApplied(
    columnFilter?: CompositeFilterDescriptor,
    field?: string,
    filterService?: any
  ): void {
    if (columnFilter) {
      const rawField = field || (columnFilter?.filters?.[0] as FilterDescriptor)?.field;
      const targetField = typeof rawField === 'string' ? rawField : rawField ? String(rawField) : undefined;
      const currentFilter = this.gridState().filter;
      const baseFilter: CompositeFilterDescriptor =
        currentFilter && Array.isArray(currentFilter.filters)
          ? { logic: currentFilter.logic ?? 'and', filters: [...currentFilter.filters] }
          : { logic: 'and', filters: [] };

      const cleaned = targetField ? this.removeFieldFilters(baseFilter, targetField) : baseFilter;

      const newLeafs = this.flattenLeafFilters(columnFilter).filter(
        (f) => (!targetField || String(f.field) === targetField) && this.hasMeaningfulFilterValue(f)
      );

      if (newLeafs.length === 1) {
        cleaned.filters.push(newLeafs[0]);
      } else if (newLeafs.length > 1) {
        cleaned.filters.push({
          logic: columnFilter.logic ?? 'and',
          filters: newLeafs,
        });
      }

      const nextFilterState = cleaned.filters.length > 0 ? cleaned : undefined;
      const next: State = { ...this.gridState(), filter: nextFilterState, skip: 0 };
      this.gridState.set(next);
      this.emitStateIfRequested(next);

      try {
        filterService?.filter?.(columnFilter);
      } catch {}
    }

    this.requestCloseColumnMenuModal();
  }

  onColumnMenuFilterCleared(field?: string, filterService?: any): void {
    if (field) {
      this.clearColumnFilter(field, filterService);
    } else {
      this.clearAllFilters(filterService);
    }
  }

  private hasMeaningfulFilterValue(f: FilterDescriptor): boolean {
    const noValueOps = ['isnull', 'isnotnull', 'isempty', 'isnotempty'];
    if (noValueOps.includes(String(f.operator ?? '').toLowerCase())) {
      return true;
    }
    if (f.value === 0 || f.value === false || f.value === true) {
      return true;
    }
    if (f.value instanceof Date) {
      return Number.isFinite(f.value.getTime());
    }
    return String(f.value ?? '').trim().length > 0;
  }

  hasGrouping(): boolean {
    return (this.gridState().group?.length ?? 0) > 0;
  }

  gridSkip(): number {
    return this.paginationEnabled() ? (this.gridState().skip ?? 0) : 0;
  }

  gridPageSize(): number {
    if (!this.paginationEnabled()) {
      return this.currentRecordCount();
    }
    const take = this.gridState().take;
    return typeof take === 'number' && Number.isFinite(take) && take > 0 ? take : 20;
  }

  pagerSettings(): false | {
    buttonCount: number;
    info: boolean;
    type: 'numeric';
    pageSizes: number[];
    previousNext: boolean;
    responsive: boolean;
  } {
    if (!this.paginationEnabled()) return false;
    return this.enabledPagerSettings;
  }

  togglePagination(enabled: boolean): void {
    this.paginationEnabled.set(enabled);
    const next: State = {
      ...this.gridState(),
      skip: 0,
      take: enabled ? this.normalizedPageSize(this.gridState().take) : this.currentRecordCount(),
    };
    this.gridState.set(next);
    this.emitStateIfRequested(next);
  }

  onPageSizeSelection(event: Event): void {
    const value = (event.target as HTMLSelectElement)?.value ?? '20';
    const take = value === 'all' ? this.currentRecordCount() : this.normalizedPageSize(Number(value));
    const next: State = { ...this.gridState(), skip: 0, take };
    this.paginationEnabled.set(true);
    this.gridState.set(next);
    this.emitStateIfRequested(next);
  }

  selectedPageSizeValue(): string {
    const take = this.gridState().take;
    if (typeof take === 'number' && [20, 50, 100].includes(take)) {
      return String(take);
    }
    if (typeof take === 'number' && take === this.currentRecordCount() && this.currentRecordCount() > 0) return 'all';
    return String(this.normalizedPageSize(take));
  }

  allGroupsToggleLabel(): string {
    return this.allGroupsCollapsed() ? 'Expand All' : 'Collapse All';
  }

  toggleAllGroups(): void {
    if (!this.hasGrouping()) return;
    this.allGroupsCollapsed.set(!this.allGroupsCollapsed());
    this.expandedGroupKeys.set(new Set());
    this.collapsedGroupKeys.set(new Set());
  }

  isGroupExpanded = (args: GroupRowArgs): boolean => {
    const key = this.groupExpansionKey(args);
    if (this.allGroupsCollapsed()) {
      return this.expandedGroupKeys().has(key);
    }
    return !this.collapsedGroupKeys().has(key);
  };

  onGroupExpand(args: GroupRowArgs): void {
    const key = this.groupExpansionKey(args);
    this.expandedGroupKeys.update((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    this.collapsedGroupKeys.update((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  onGroupCollapse(args: GroupRowArgs): void {
    const key = this.groupExpansionKey(args);
    this.collapsedGroupKeys.update((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    this.expandedGroupKeys.update((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  groupHeaderText(value: unknown, group: GroupResult | undefined): string {
    const label = value == null || value === '' ? '(blank)' : String(value);
    return `${label} (ct. ${this.groupRecordCount(group)})`;
  }

  groupRecordCount(group: GroupResult | undefined): number {
    if (!group || !Array.isArray(group.items)) return 0;
    return group.items.reduce<number>((sum, item) => {
      const nested = item as GroupResult;
      if (nested && Array.isArray(nested.items) && typeof nested.field === 'string') {
        return sum + this.groupRecordCount(nested);
      }
      return sum + 1;
    }, 0);
  }

  onGridColumnsChanged(): void {
    this.gridColumnsTick.update((v) => v + 1);
  }

  hasAnyFilters(): boolean {
    return this.hasAnyFilterIn(this.gridState().filter);
  }

  filterIndicator(): { total: number; hidden: number; missing: number } {
    const infos = this.leafFilterInfos();
    if (infos.length === 0) return { total: 0, hidden: 0, missing: 0 };

    const hidden = infos.filter((i) => i.status === 'hidden').length;
    const missing = infos.filter((i) => i.status === 'missing').length;
    return { total: infos.length, hidden, missing };
  }

  effectiveColumns(): any[] {
    const base = Array.isArray((this.gridSettings as any)?.columns)
      ? ((this.gridSettings as any).columns as any[])
      : [];
    const overrides = this.columnOverridesSig();
    const widthOverrides = this.columnWidthOverridesSig();
    const patchedBase = base.map((c) => {
      const field = String(c?.field ?? '');
      const o = field ? overrides[field] : undefined;
      return o ? { ...c, ...o } : c;
    });
    const merged = [...patchedBase, ...this.addedColumnsSig()];
    return merged.map((c) => {
      const field = String(c?.field ?? '');
      if (!field) return c;
      const w = widthOverrides[field];
      return typeof w === 'number' && Number.isFinite(w) ? { ...c, width: w } : c;
    });
  }

  buildGridCopyText(): string {
    const visibleColumns = this.effectiveColumns().filter((column) => !column?.hidden && column?.field);
    const visibleRows = this.visibleGridRows();
    const title = String((this.gridSettings as any)?.title ?? 'Grid Data').trim() || 'Grid Data';
    const lines = [title, `Rows: ${visibleRows.length}`];

    if (!visibleColumns.length || !visibleRows.length) {
      lines.push('', 'No rows to copy.');
      return lines.join('\n');
    }

    lines.push('');
    for (const [index, row] of visibleRows.entries()) {
      lines.push(`Row ${index + 1}`);
      for (const column of visibleColumns) {
        const label = String(column.title ?? this.humanizeField(String(column.field)));
        lines.push(`${label}: ${this.formatGridCellValue(column, row)}`);
      }
      if (index < visibleRows.length - 1) {
        lines.push('');
      }
    }

    return lines.join('\n');
  }

  async copyGridToClipboard(): Promise<void> {
    const copied = await this.clipboardService.copyText(this.buildGridCopyText());
    await this.toast(copied ? 'Grid data copied to clipboard.' : 'Unable to copy grid data.', copied ? 'success' : 'danger');
  }

  adjustColumnWidth(
    direction: 'bigger' | 'smaller',
    field: string | null | undefined,
    menuService?: any
  ): void {
    if (!field) {
      try {
        menuService?.close?.();
      } catch {}
      return;
    }

    const stepPx = 50;
    const minPx = 40;

    this.columnWidthOverridesSig.update((prev) => {
      const current =
        typeof prev[field] === 'number' && Number.isFinite(prev[field])
          ? prev[field]
          : (this.baseColumnWidth(field) ?? 80);
      const next =
        direction === 'bigger'
          ? current + stepPx
          : Math.max(minPx, current - stepPx);
      if (next === prev[field]) return prev;
      return { ...prev, [field]: next };
    });

    try {
      menuService?.close?.();
    } catch {}
  }

  resetColumnWidth(field: string | null | undefined, menuService?: any): void {
    if (!field) {
      try {
        menuService?.close?.();
      } catch {}
      return;
    }

    this.columnWidthOverridesSig.update((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

    try {
      menuService?.close?.();
    } catch {}
  }

  openFilters(event: Event): void {
    event.stopPropagation();
    this.usePopoverFilters = this.computeUsePopoverFilters();
    this.filtersEvent = event;
    this.filtersButtons = this.buildFiltersButtons();
    this.filtersModalOpen = true;
  }

  async closeFilters(event?: Event, fromDismiss = false): Promise<void> {
    event?.stopPropagation();
    this.filtersModalOpen = false;
    if (fromDismiss) {
      this.filtersEvent = undefined;
    } else {
      try {
        await this.filtersPopover?.dismiss();
      } catch {}
    }
  }

  filtersMenuItems(): Array<{
    id: string;
    kind: 'add' | 'show' | 'clear' | 'close' | 'noop';
    field?: string;
    text: string;
    icon?: string;
    disabled?: boolean;
  }> {
    const infos = this.leafFilterInfos();
    const missing = infos.filter((i) => i.status === 'missing');
    const hidden = infos.filter((i) => i.status === 'hidden');

    const byField = (arr: typeof infos) => {
      const m = new Map<string, typeof infos>();
      for (const it of arr) {
        const key = String(it.field);
        if (!m.has(key)) m.set(key, []);
        m.get(key)!.push(it);
      }
      return m;
    };

    const items: Array<{
      id: string;
      kind: 'add' | 'show' | 'clear' | 'close' | 'noop';
      field?: string;
      text: string;
      icon?: string;
      disabled?: boolean;
    }> = [];

    for (const [field, arr] of byField(missing)) {
      const first = arr[0]!;
      items.push({
        id: `add:${field}`,
        kind: 'add',
        field,
        icon: 'add-outline',
        text: `Add column for ${first.title} (${this.formatLeafFilter(first)})`,
      });
    }

    for (const [field, arr] of byField(hidden)) {
      const first = arr[0]!;
      items.push({
        id: `show:${field}`,
        kind: 'show',
        field,
        icon: 'eye-outline',
        text: `Show column ${first.title} (${this.formatLeafFilter(first)})`,
      });
    }

    if (items.length === 0) {
      items.push({
        id: 'noop',
        kind: 'noop',
        icon: 'checkmark-outline',
        text: 'No hidden/missing filter columns',
        disabled: true,
      });
    }

    items.push({
      id: 'clear',
      kind: 'clear',
      icon: 'close-circle-outline',
      text: 'Clear all Filters',
      disabled: infos.length === 0,
    });

    items.push({
      id: 'close',
      kind: 'close',
      icon: 'close',
      text: 'Close',
    });

    return items;
  }

  onFiltersMenuItemClick(
    mi: { kind: string; field?: string; disabled?: boolean },
    event?: Event
  ): void {
    event?.stopPropagation();
    if (mi.disabled) return;

    const field = mi.field ? String(mi.field) : undefined;

    if (mi.kind === 'add' && field) {
      this.addSessionColumn(field);
      void this.closeFilters();
      return;
    }
    if (mi.kind === 'show' && field) {
      this.showColumn(field);
      void this.closeFilters();
      return;
    }
    if (mi.kind === 'clear') {
      this.clearAllFilters();
      void this.closeFilters();
      return;
    }
    if (mi.kind === 'close') {
      void this.closeFilters();
      return;
    }
  }

  getColumnSortDir(field: string | null | undefined): 'asc' | 'desc' | null {
    if (!field) return null;
    const currentSort = this.gridState().sort ?? [];
    const match = currentSort.find((s) => s.field === field);
    return match ? (match.dir ?? null) : null;
  }

  setColumnSort(
    field: string | null | undefined,
    dir: 'asc' | 'desc' | 'clear',
    menuService?: any
  ): void {
    if (!field) return;

    const currentSort = [...(this.gridState().sort ?? [])];
    const index = currentSort.findIndex((s) => s.field === field);

    if (dir === 'clear') {
      if (index >= 0) {
        currentSort.splice(index, 1);
      }
    } else {
      if (index >= 0) {
        currentSort[index] = { field, dir };
      } else {
        currentSort.push({ field, dir });
      }
    }

    const next: State = { ...this.gridState(), sort: currentSort };
    this.gridState.set(next);
    this.emitStateIfRequested(next);

    try {
      menuService?.close?.();
    } catch {}
    this.requestCloseColumnMenuModal();
  }

  autoFitCurrentColumn(column: any, menuService?: any): void {
    if (this.grid && column) {
      try {
        this.grid.autoFitColumn(column);
      } catch (err) {
        console.warn('Could not auto-fit column:', err);
      }
    }
    try {
      menuService?.close?.();
    } catch {}
    this.requestCloseColumnMenuModal();
  }

  autoFitAllGridColumns(menuService?: any): void {
    if (this.grid) {
      try {
        this.grid.autoFitColumns();
      } catch (err) {
        console.warn('Could not auto-fit all columns:', err);
      }
    }
    try {
      menuService?.close?.();
    } catch {}
    this.requestCloseColumnMenuModal();
  }

  hasColumnFilter(field: string | null | undefined): boolean {
    if (!field) return false;
    const leaves = this.flattenLeafFilters(this.gridState().filter);
    return leaves.some((l) => l.field === field);
  }

  clearColumnFilter(field: string | null | undefined, menuService?: any): void {
    if (!field) return;
    const currentFilter = this.gridState().filter;
    if (!currentFilter || !Array.isArray(currentFilter.filters)) {
      this.clearAllFilters(menuService);
      return;
    }

    const removeFieldFilter = (filters: any[]): any[] => {
      return filters
        .filter((f) => {
          if (f.field) return f.field !== field;
          if (f.filters) return true;
          return true;
        })
        .map((f) => {
          if (f.filters) {
            return { ...f, filters: removeFieldFilter(f.filters) };
          }
          return f;
        })
        .filter((f) => !f.filters || f.filters.length > 0);
    };

    const nextFilters = removeFieldFilter(currentFilter.filters);
    const nextFilterState = nextFilters.length > 0 ? { ...currentFilter, filters: nextFilters } : undefined;
    const next: State = { ...this.gridState(), filter: nextFilterState, skip: 0 };
    this.gridState.set(next);
    this.emitStateIfRequested(next);

    try {
      menuService?.close?.();
    } catch {}
    this.requestCloseColumnMenuModal();
  }

  clearAllFilters(menuService?: any): void {
    const next: State = { ...this.gridState(), filter: undefined, skip: 0 };
    this.gridState.set(next);
    this.emitStateIfRequested(next);

    try {
      menuService?.close?.();
    } catch {}
    this.requestCloseColumnMenuModal();
  }

  shouldUseColumnMenuModal(): boolean {
    return true;
  }

  captureAndOpenColumnMenuModal(service: any, column: any): boolean {
    if (!this.shouldUseColumnMenuModal()) return false;
    if (this.columnMenuModalOpen || this.columnMenuModalOpening) return false;
    this.openColumnMenuModal(service, column);
    return false; // used from template in an *ngIf guard
  }

  requestCloseColumnMenuModal(): void {
    this.columnMenuModalOpen = false;
  }

  onColumnMenuModalDidDismiss(): void {
    // Keep the controlled `isOpen` flag in sync even when the user dismisses via backdrop.
    this.columnMenuModalOpen = false;

    const svc = this.columnMenuModalService;
    const originalClose = this.columnMenuModalOriginalClose;

    // Best-effort: restore original service.close if we patched it.
    try {
      if (svc && typeof (svc as any).__ionModalOriginalClose === 'function') {
        svc.close = (svc as any).__ionModalOriginalClose;
        delete (svc as any).__ionModalOriginalClose;
        delete (svc as any).__ionModalClosePatched;
      }
    } catch {}

    // Best-effort: close the Kendo popup if it was still open.
    try {
      originalClose?.();
    } catch {}

    this.columnMenuModalTitle = '';
    this.columnMenuModalService = null;
    this.columnMenuModalColumn = null;
    this.columnMenuModalOriginalClose = null;
    this.columnMenuModalOpening = false;
  }

  private openColumnMenuModal(service: any, column: any): void {
    this.columnMenuModalOpening = true;

    const title = String(column?.title ?? column?.field ?? 'Column');
    const originalClose =
      service && typeof service.close === 'function' ? service.close.bind(service) : null;

    this.columnMenuModalTitle = title;
    this.columnMenuModalService = service ?? null;
    this.columnMenuModalColumn = column ?? null;
    this.columnMenuModalOriginalClose = originalClose;

    Promise.resolve().then(() => {
      // Close the Kendo popup menu first (prevents a tiny, hard-to-use overlay on mobile).
      try {
        originalClose?.();
      } catch {}

      // Patch service.close so any Kendo menu items that call close() will dismiss the modal.
      try {
        if (service && !(service as any).__ionModalClosePatched && originalClose) {
          (service as any).__ionModalClosePatched = true;
          (service as any).__ionModalOriginalClose = originalClose;
          service.close = () => {
            try {
              originalClose();
            } catch {}
            this.requestCloseColumnMenuModal();
          };
        }
      } catch {}

      this.columnMenuModalOpen = true;
      this.columnMenuModalOpening = false;
    });
  }

  private hasAnyFilterIn(
    filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined
  ): boolean {
    if (!filter) return false;

    const composite = filter as CompositeFilterDescriptor;
    if (Array.isArray(composite.filters)) {
      return composite.filters.some((f) => this.hasAnyFilterIn(f as any));
    }

    const leaf = filter as FilterDescriptor;
    return !!leaf.field;
  }

  private flattenLeafFilters(
    filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined
  ): FilterDescriptor[] {
    if (!filter) return [];

    const composite = filter as CompositeFilterDescriptor;
    if (Array.isArray(composite.filters)) {
      return composite.filters.flatMap((f) => this.flattenLeafFilters(f as any));
    }

    const leaf = filter as FilterDescriptor;
    return leaf?.field ? [leaf] : [];
  }

  private computeUsePopoverFilters(): boolean {
    if (Capacitor.isNativePlatform()) return false;
    return !this.isMobileDimensions();
  }

  private isMobileDimensions(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(max-width: 720px)')?.matches ?? false;
  }

  private buildFiltersButtons(): ActionSheetButton[] {
    const items = this.filtersMenuItems();
    const buttons: ActionSheetButton[] = items
      .filter((i) => i.kind !== 'close' && i.kind !== 'noop')
      .map((i) => ({
        text: i.text,
        icon: i.icon,
        handler: () => {
          this.onFiltersMenuItemClick(i);
          return true;
        },
      }));

    buttons.push({ text: 'Cancel', role: 'cancel' });
    return buttons;
  }

  private addSessionColumn(field: string): void {
    const existing = this.findEffectiveColumn(field);
    if (existing) {
      if (existing.hidden) this.showColumn(field);
      return;
    }

    const title = this.humanizeField(field);
    this.addedColumnsSig.update((prev) => {
      if (prev.some((c) => String(c?.field ?? '') === field)) return prev;
      return [...prev, { field, title, width: 120, hidden: false }];
    });

    void this.toast(`Added column "${title}"`, 'success');
  }

  private showColumn(field: string): void {
    this.columnOverridesSig.update((prev) => ({
      ...prev,
      [field]: { ...(prev[field] ?? {}), hidden: false },
    }));
    void this.toast(`Showing column "${this.humanizeField(field)}"`, 'medium');
  }

  private findEffectiveColumn(field: string): any | null {
    const cols = this.effectiveColumns();
    return cols.find((c) => String(c?.field ?? '') === field) ?? null;
  }

  private leafFilterInfos(): Array<{
    field: string;
    operator?: string;
    value?: any;
    status: 'visible' | 'hidden' | 'missing';
    title: string;
  }> {
    this.gridColumnsTick();
    const leaves = this.flattenLeafFilters(this.gridState().filter);
    if (leaves.length === 0) return [];

    const gridByField = this.gridColumnsByField();
    const cfgCols = this.effectiveColumns();
    const cfgByField = new Map<string, any>();
    for (const c of cfgCols) {
      const f = String(c?.field ?? '');
      if (f) cfgByField.set(f, c);
    }

    return leaves.map((leaf) => {
      const field = String(leaf.field);
      const gridCol = gridByField?.get(field);
      const cfgCol = cfgByField.get(field);
      const title = String(gridCol?.title ?? cfgCol?.title ?? this.humanizeField(field));
      const status: 'visible' | 'hidden' | 'missing' = gridByField
        ? (!gridCol ? 'missing' : (gridCol.hidden ? 'hidden' : 'visible'))
        : (!cfgCol ? 'missing' : (cfgCol?.hidden ? 'hidden' : 'visible'));
      return { field, operator: leaf.operator as any, value: (leaf as any).value, status, title };
    });
  }

  private gridColumnsByField(): Map<string, { hidden: boolean; title?: string }> | null {
    const gridCols = (this.grid as any)?.columns?.toArray?.() as any[] | undefined;
    if (!Array.isArray(gridCols) || gridCols.length === 0) return null;

    const m = new Map<string, { hidden: boolean; title?: string }>();
    for (const c of gridCols) {
      const f = String(c?.field ?? '');
      if (!f) continue;
      m.set(f, { hidden: !!c?.hidden, title: c?.title });
    }
    return m;
  }

  private formatLeafFilter(x: { operator?: string; value?: any }): string {
    const op = String(x.operator ?? '');
    const opText =
      op === 'eq' ? 'equals' :
      op === 'neq' ? 'not equal' :
      op === 'contains' ? 'contains' :
      op === 'doesnotcontain' ? 'does not contain' :
      op === 'startswith' ? 'starts with' :
      op === 'endswith' ? 'ends with' :
      op === 'gte' ? '>=' :
      op === 'gt' ? '>' :
      op === 'lte' ? '<=' :
      op === 'lt' ? '<' :
      op === 'isnull' ? 'is null' :
      op === 'isnotnull' ? 'is not null' :
      op === 'isempty' ? 'is empty' :
      op === 'isnotempty' ? 'is not empty' :
      op || 'filter';

    const v = x.value;
    const vText =
      v === null ? 'null' :
      typeof v === 'string' ? v :
      typeof v === 'number' || typeof v === 'boolean' ? String(v) :
      Array.isArray(v) ? v.map((z) => String(z)).join(', ') :
      v instanceof Date ? v.toISOString() :
      typeof v === 'object' ? JSON.stringify(v) :
      String(v);

    if (op === 'isnull' || op === 'isnotnull' || op === 'isempty' || op === 'isnotempty') {
      return opText;
    }
    return `${opText} ${vText}`;
  }

  private humanizeField(field: string): string {
    const last = String(field ?? '').split('.').filter(Boolean).pop() ?? String(field ?? '');
    const spaced = last
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
    return spaced.replace(/\bId\b/g, 'ID');
  }

  private baseColumnWidth(field: string): number | null {
    const base = Array.isArray((this.gridSettings as any)?.columns)
      ? ((this.gridSettings as any).columns as any[])
      : [];
    const added = this.addedColumnsSig();
    const col =
      base.find((c) => String(c?.field ?? '') === field) ??
      added.find((c) => String(c?.field ?? '') === field);

    const raw = col?.width;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
      const n = Number.parseFloat(raw);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  private currentRecordCount(): number {
    const records = this.usingSignals ? this.recordsAsSignals?.() : this.records;
    return Math.max(Array.isArray(records) ? records.length : 0, 1);
  }

  private visibleGridRows(): any[] {
    const source = this.usingSignals ? this.newMappedSignals() : this.records;
    const data = Array.isArray(source) ? source : [];
    const state = this.gridState();
    const processed = process(data, {
      sort: state.sort,
      filter: state.filter,
    }).data;

    const flatRows = Array.isArray(processed) ? processed : [];
    if (!this.paginationEnabled()) {
      return flatRows;
    }

    const skip = this.gridSkip();
    const take = this.gridPageSize();
    return flatRows.slice(skip, skip + take);
  }

  private formatGridCellValue(column: any, row: any): string {
    const value = this.resolvePath(row, column.field);
    if (column?.type === 'date') {
      const siteId = this.resolveDateSiteId(row);
      const transformed = this.mobileTimezoneService.format(
        value,
        this.gridDateFormat(column),
        siteId ?? undefined,
        true
      );
      return transformed ? String(transformed) : '—';
    }

    const pill = this.statusPill(column, row);
    if (pill) {
      if (this.shouldShowStatusCategory(column, row)) {
        const cat = this.getStatusCategory(row);
        return `${pill.text} (${cat})`;
      }
      return pill.text;
    }

    return this.formatValue(value);
  }

  private formatValue(value: unknown): string {
    if (value == null || value === '') return '—';
    if (Array.isArray(value)) return value.map((item) => this.formatValue(item)).join(', ');
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  private normalizedPageSize(value: unknown): number {
    const n = typeof value === 'number' && Number.isFinite(value) ? value : Number(value);
    return [20, 50, 100].includes(n) ? n : 20;
  }

  private emitStateIfRequested(next: State): void {
    if (this.feedStateBackToParent) {
      this.stateChange.emit(next);
    }
  }

  private resetGroupExpansionState(): void {
    this.allGroupsCollapsed.set(false);
    this.expandedGroupKeys.set(new Set());
    this.collapsedGroupKeys.set(new Set());
  }

  private groupSignature(groups: State['group']): string {
    return JSON.stringify((groups ?? []).map((group) => ({
      field: group.field,
      dir: group.dir,
    })));
  }

  private groupExpansionKey(args: GroupRowArgs): string {
    const parts: string[] = [];
    let current: GroupRowArgs | undefined = args;
    while (current) {
      parts.unshift(`${current.group?.field ?? ''}:${String(current.group?.value ?? '')}`);
      current = current.parentGroup;
    }
    return parts.join('|') || args.groupIndex;
  }

updateRowColor: RowClassFn = (context: RowClassArgs) => {
    const tagId = this.getRowTagId(context.dataItem);
    const inBucket = typeof tagId === 'number' && this.tagCacheService.isInBucket(tagId);

    if (this.currentDataIndex !== undefined) {
      if (context.index === this.currentDataIndex) {
        return { 'highlight-row': true, 'dim-row': false, 'tag-in-bucket': inBucket };
      } else {
        return { 'highlight-row': false, 'dim-row': true, 'tag-in-bucket': inBucket };
      }
    } else {
      const recentlyUpdated = context.dataItem.RecentlyUpdated;
      const recentlyUpdatedWarningTag =
        context.dataItem.RecentlyUpdated &&
        context.dataItem.Severity == 'Warning' &&
        context.dataItem.Value == context.dataItem.ValueWhenActive;
      const recentlyUpdatedAlarmTag =
        context.dataItem.RecentlyUpdated &&
        context.dataItem.Severity == 'Alarm' &&
        context.dataItem.Value == context.dataItem.ValueWhenActive;
      const recentlyUpdatedCriticalTag =
        context.dataItem.RecentlyUpdated &&
        context.dataItem.Severity == 'Critical' &&
        context.dataItem.Value == context.dataItem.ValueWhenActive;

      return {
        recentlyUpdated,
        recentlyUpdatedWarningTag,
        recentlyUpdatedAlarmTag,
        recentlyUpdatedCriticalTag,
        'tag-in-bucket': inBucket,
      };
    }
  };

  onRightClick(event: MouseEvent, tag: ITag): void {
    event.preventDefault(); // prevent browser context menu
    this.showContextMenu(tag, event.clientX, event.clientY);
  }

  onTouchStart(event: TouchEvent, tag: ITag): void {
    this.longPressTimer = setTimeout(() => {
      this.showContextMenu(tag, event.touches[0].clientX, event.touches[0].clientY);
    }, this.longPressDuration);
  }

  onTouchEnd(event: TouchEvent): void {
    clearTimeout(this.longPressTimer);
  }

  showContextMenu(tag: ITag, x: number, y: number): void {
    // Option 1: Navigate to tag modal route
    console.log('Showing context menu for tag:', tag);
    // this.router.navigate(['/tag-modal'], {
    //   queryParams: { tags: tag.Id }
    // });
    this.router.navigate(['/tag-modal'], {
      queryParams: { tags: tag.Id } // change if supporting multiple
    });

    // Option 2: Or use ModalController if inside an Ionic modal context
    // const modal = await this.modalCtrl.create({
    //   component: TagModalComponent,
    //   componentProps: { tag }
    // });
    // await modal.present();
  }

  onCellClick(event: any): void {
    if (this.isTrendIgnoreEvent(event?.originalEvent)) return;

    const item = event?.dataItem;
    if (!item) return;

    // If you only want this behavior when grid shows tag/alarm content:
    // if (!this.tagGrid) return;

    // 1) Tag rows (ITag): add tag w/out dates (existing behavior)
    if (this.isTagRow(item)) {
      if (this.tagCacheService.isInBucket(item.Id)) {
        this.tagCacheService.removeFromBucketById(item.Id);
        this.toast(`Removed "${item.ShortTagName}" from tag bucket`, 'medium');
      } else {
        this.tagCacheService.addToBucket(item);
        this.toast(`Added "${item.ShortTagName}" to tag bucket`, 'success');
      }
      return;
    }

    // 2) Alarm rows: add tag with per-row date range
    if (this.hasAlarmShape(item)) {
      const tagId = this.coerceTagId(item);
      if (!tagId) { this.toast('No TagId on alarm row', 'warning'); return; }

      if (this.tagCacheService.isInBucket(tagId)) {
        this.tagCacheService.removeFromBucketById(tagId);
        this.toast(`Removed Tag ${tagId} from tag bucket`, 'medium');
        return;
      }

      // Try to resolve the tag from cache (preferred), but allow alarm-record rows
      // to trend even when the underlying ITag isn't loaded in memory yet.
      let tag = this.findTag(tagId);
      if (!tag) {
        this.tagCacheService.ensureTagIdIndexed(tagId);
        tag = this.findTag(tagId);
      }
      if (!tag) {
        tag = this.pseudoTagFromAlarmRow(item, tagId);
      }

      const startMs = this.coerceStartMs(item);
      const endMs = this.coerceEndMs(item);

      this.tagCacheService.addToBucket(tag, startMs, endMs);
      const name = tag.ShortTagName ?? `Tag ${tag.Id}`;
      const when =
        (startMs ? new Date(startMs).toLocaleString() : '—') +
        ' → ' +
        (endMs ? new Date(endMs).toLocaleString() : '—');

      this.toast(`Added "${name}" with range ${when}`, 'success');
      return;
    }

    // 3) Fallback (other row types): do nothing or keep your previous navigation
    // this.onTagSelected(item as ITag); // <- only if you know it's a tag
  }

  openTagModal(tag: ITag): void {
    // Option 1: Navigate
    console.log('Opening tag modal for tag:', tag);
    this.router.navigate(['/tag-modal'], {
      queryParams: { tags: tag.Id } // change if supporting multiple
    });
    // this.router.navigate(['/tag-modal'], {
    //   queryParams: { tags: tag.Id }
    // });

    // Option 2 (if you're in Ionic and want modal stacking)
    // const modal = await this.modalCtrl.create({
    //   component: TagModalComponent,
    //   componentProps: { tag }
    // });
    // await modal.present();
  }

  async onTagSelected(tag: ITag) {
    this.tagCacheService.addToBucket(tag);

    const toast = await this.toastCtrl.create({
      message: `Added "${tag.ShortTagName}" to tag bucket`,
      duration: 2000,
      position: 'bottom',
      color: 'success'
    });
    await toast.present();
  }

  public allData(): ExcelExportData {
    const data = this.usingSignals ? this.newMappedSignals() : this.records;
    const s = this.gridState();
    return {
      data: process(data, { group: s.group, sort: s.sort, filter: s.filter }).data,
      group: s.group,
    };
  }

  private isTagRow(x: any): x is ITag {
    return x && typeof x.Id === 'number' && typeof x.ShortTagName !== 'undefined';
  }

  private getRowTagId(x: any): number | undefined {
    if (this.isTagRow(x)) return x.Id;
    if (this.hasAlarmShape(x)) return this.coerceTagId(x);
    return undefined;
  }

  /** Accepts multiple common alarm shapes (Historical/Playback) */
  private hasAlarmShape(x: any): boolean {
    if (!x || typeof x !== 'object') return false;

    // prefer explicit ms fields (Historical)
    const hasMs = (typeof x.TagId === 'number') &&
      (typeof x.AlarmStartDateUTCMS === 'number' || typeof x.StartDateUTCMS === 'number') &&
      (typeof x.AlarmEndDateUTCMS === 'number' || typeof x.EndDateUTCMS === 'number' || x.AlarmEndDateUTCMS === null || x.EndDateUTCMS === null);

    // fallback: tagId + ISO/date values (Playback rows – recommend also passing ms)
    const hasStringDates = (typeof x.tagId === 'number' || typeof x.TagId === 'number') &&
      (typeof x.start === 'string' || x.start instanceof Date || typeof x.StartDateUTC === 'string' || x.StartDateUTC instanceof Date || x.AlarmStartDateUTC instanceof Date) &&
      (typeof x.end === 'string' || x.end instanceof Date || typeof x.EndDateUTC === 'string' || x.EndDateUTC instanceof Date || x.AlarmEndDateUTC instanceof Date || x.end === '—');

    return hasMs || hasStringDates;
  }

  private coerceTagId(x: any): number | undefined {
    return typeof x.TagId === 'number' ? x.TagId :
      typeof x.tagId === 'number' ? x.tagId :
        undefined;
  }

  private coerceStartMs(x: any): number | undefined {
    // ms fields from Historical variants
    if (typeof x.AlarmStartDateUTCMS === 'number') return x.AlarmStartDateUTCMS;
    if (typeof x.StartDateUTCMS === 'number') return x.StartDateUTCMS;

    // Date objects
    if (x.AlarmStartDateUTC instanceof Date) return x.AlarmStartDateUTC.getTime();
    if (x.StartDateUTC instanceof Date) return x.StartDateUTC.getTime();
    if (x.start instanceof Date) return x.start.getTime();

    // ISO/string fields (Playback rows) – strongly recommend you also include ms fields
    const iso = typeof x.StartDateUTC === 'string' ? x.StartDateUTC :
      typeof x.start === 'string' ? x.start : undefined;
    const t = iso ? Date.parse(iso) : NaN;
    return Number.isFinite(t) ? t : undefined;
  }

  private coerceEndMs(x: any): number | undefined {
    // ms fields
    if (typeof x.AlarmEndDateUTCMS === 'number') return x.AlarmEndDateUTCMS;
    if (typeof x.EndDateUTCMS === 'number') return x.EndDateUTCMS;

    // nullable end
    if (x.AlarmEndDateUTCMS === null || x.EndDateUTCMS === null) return undefined;

    // Date objects
    if (x.AlarmEndDateUTC instanceof Date) return x.AlarmEndDateUTC.getTime();
    if (x.EndDateUTC instanceof Date) return x.EndDateUTC.getTime();
    if (x.end instanceof Date) return x.end.getTime();

    // ISO/string fields
    const iso = typeof x.EndDateUTC === 'string' ? x.EndDateUTC :
      typeof x.end === 'string' && x.end !== '—' ? x.end : undefined;
    const t = iso ? Date.parse(iso) : NaN;
    return Number.isFinite(t) ? t : undefined;
  }

  private findTag(tagId: number): ITag | null {
    // Prefer cached signal for speed; fallback to records if needed
    const sig = this.tagCacheService.getTagByIdSignal(tagId);
    const fromSig = sig?.();
    if (fromSig) return fromSig;

    const list = this.usingSignals ? this.newMappedSignals() : this.records;
    return list.find(t => t.Id === tagId) ?? null;
  }

  private pseudoTagFromAlarmRow(row: any, tagId: number): ITag {
    const name = this.coerceAlarmRowName(row, tagId);
    const assetName = row?.AssetName ?? row?.Asset?.Name ?? '';
    const parentSystemName = row?.GateName ?? row?.Asset?.ParentSystem?.Name ?? '';
    const siteName = row?.SiteName ?? row?.Asset?.Site?.Name ?? '';
    const assetId = row?.AssetId ?? row?.Asset?.Id ?? 0;
    const siteId = row?.SiteId ?? row?.Asset?.Site?.Id;

    return {
      Id: tagId,
      Name: name,
      ShortTagName: name,
      IsAlarm: true,
      AssetId: assetId,
      SiteId: siteId,
      Asset: {
        Id: assetId,
        Name: assetName,
        ParentSystem: { Name: parentSystemName },
        Site: { Id: siteId, Name: siteName },
      },
    } as unknown as ITag;
  }

  private coerceAlarmRowName(row: any, tagId: number): string {
    return (
      row?.TagName ??
      row?.AlarmName ??
      row?.ShortTagName ??
      row?.Name ??
      `Tag ${tagId}`
    );
  }

  private async toast(message: string, color: 'success' | 'medium' | 'warning' | 'danger' | 'primary' | 'tertiary' = 'success') {
    const t = await this.toastCtrl.create({ message, duration: 1800, position: 'bottom', color });
    await t.present();
  }
}
